import { useTranslation } from '@msdk/common'
import {
  ControlValidationType,
  EndToEndSeparator,
  FormGroupDefinition,
  FormNode,
  formTraverse,
  GroupStyle,
  Separator,
} from '@msdk/definitions'
import {
  Box,
  CardBody,
  CardHeader,
  COLORS,
  createComponentWrapperWithRef,
  Divider,
  FormCard as BaseFormCard,
  IconSelector,
  styled,
  Tooltip,
  Typography,
  uiTokens,
} from '@msdk/ui'
import React, { ReactNode } from 'react'

import { nodeHasFlag } from '../../../utils'
import { getWidthOfElement } from './utils'

const StyledMuiBox = styled(Box)`
  .MuiCardContent-root {
    padding-bottom: 0;
  }
  .ConditionalField-renderer {
    padding-bottom: 16px;
  }
  .ConditionalField-renderer:has(> div:only-child:empty),
  .ConditionalField-renderer:has(> div:only-child > div:only-child:empty),
  .ConditionalField-renderer:not(:has(> *)) {
    padding-bottom: 0;
  }
`
const StyledNovaBox = styled(Box)`
  .MuiCardContent-root {
    padding-bottom: 0;
  }
  .ConditionalField-renderer {
    padding-bottom: ${uiTokens.spacing.padding.large}px;
  }
  .ConditionalField-renderer:has(> div:only-child:empty),
  .ConditionalField-renderer:has(> div:only-child > div:only-child:empty),
  .ConditionalField-renderer:not(:has(> *)) {
    padding-bottom: 0;
  }
`
const FormGroupItem = createComponentWrapperWithRef(
  {
    mui: StyledMuiBox,
    nova: StyledNovaBox,
  },
  'FormGroupItem',
)

const MuiStyledCardBody = styled(CardBody)`
  display: flex;
  flex-flow: wrap;
  flex-direction: row;
  padding: 0;
  width: 100%;
  .MuiCardContent-root {
    padding-bottom: 0;
  }
`

const NovaStyledCardBody = styled(CardBody)`
  display: flex;
  flex-flow: wrap;
  flex-direction: row;
  padding: 0;
  width: 100%;
  .MuiCardContent-root {
    padding-bottom: 0;
  }
  &:last-child {
    padding-bottom: 0;
  }
`

const FormGroupContent = createComponentWrapperWithRef(
  {
    mui: MuiStyledCardBody,
    nova: NovaStyledCardBody,
  },
  'FormGroupContent',
)

const StyledSeparator = styled(Divider)`
  margin-bottom: 24px;
`
const StyledEndToEndSeparator = styled(Divider)`
  margin-bottom: 24px;
  margin-left: -24px;
  margin-right: -24px;
`

const HeaderMuiWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  margin-bottom: 12px;
`

const HeaderNovaWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  margin-bottom: 24px;
`
const HeaderWrapper = createComponentWrapperWithRef(
  {
    mui: HeaderMuiWrapper,
    nova: HeaderNovaWrapper,
  },
  'HeaderWrapper',
)

const TitleWrapper = styled('span')`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`

const IconContainer = styled('div')`
  height: 24px;
  width: 24px;
`

const StyledMuiCardHeader = styled(CardHeader, { shouldForwardProp: (prop) => prop !== '$groupStyle' })<{
  $groupStyle: GroupStyle
}>`
  ${(props) => props.$groupStyle === GroupStyle.SubGroup && `color: ${COLORS.text.secondary};`}
  padding: 0;

  .MuiCardHeader-title {
    color: inherit;
  }
`
const StyledNovaCardHeader = styled(CardHeader, { shouldForwardProp: (prop) => prop !== '$groupStyle' })<{
  $groupStyle: GroupStyle
}>`
  ${(props) => props.$groupStyle === GroupStyle.SubGroup && `color: ${COLORS.text.secondary};`}
  padding: 0;

  .MuiCardHeader-title {
    color: ${uiTokens.uiContent.neutral};
    padding-top: 0;
    padding-bottom: 0;
    ${(props) =>
      props.$groupStyle === GroupStyle.SubGroup &&
      `font-size: ${uiTokens.font.fontSize.body.medium}px; font-weight: ${uiTokens.font.fontWeight.regular}`}
  }
`
const StyledCardHeader = createComponentWrapperWithRef(
  {
    mui: StyledMuiCardHeader,
    nova: StyledNovaCardHeader,
  },
  'StyledCardHeader',
)

const StyledMuiWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== '$groupStyle' })<{
  $groupStyle: GroupStyle
}>`
  padding-bottom: 0px;
  ${(props) =>
    props.$groupStyle === GroupStyle.CenterGroup &&
    `
    padding: 16px 24px;
    border: 1px solid ${COLORS.border};
    border-radius: 8px;
    background-color: ${COLORS.components.form.group.background};
  `}
`

const StyledNovaWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== '$groupStyle' })<{
  $groupStyle: GroupStyle
}>`
  padding-bottom: 8px;
  ${(props) =>
    props.$groupStyle === GroupStyle.CenterGroup &&
    `
    padding: ${uiTokens.spacing.padding.medium}px;
    border: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
    border-radius: ${uiTokens.radius.large}px;
    background-color: ${uiTokens.uiSurface['wm-static']};
    align-items: flex-start;
    gap: ${uiTokens.spacing.gap.large}px;
  `}
`
const StyledWrapper = createComponentWrapperWithRef(
  {
    mui: StyledMuiWrapper,
    nova: StyledNovaWrapper,
  },
  'StyledWrapper',
)

const Mandatory = styled('span')`
  color: ${COLORS.error.main};
  margin-left: 5px;
`

/**
 * Right padding fix
 */
const FormGroupContentSlot = styled(CardBody)`
  display: flex;
  flex-flow: wrap;
  .MuiCardContent-root {
    padding: 0;
  }
`

interface FormCardSlotContextValue {
  children?: ReactNode[]
  getWidth: (index: number) => ReturnType<typeof getWidthOfElement>
}

const FormCardSlotContext = React.createContext<FormCardSlotContextValue | undefined>(undefined)

const ContentSlot = () => {
  const context = React.useContext(FormCardSlotContext)
  if (!context) return null

  return (
    <FormGroupContentSlot className="FormGroup__content">
      {context.children?.map((element, index) => (
        <FormGroupItem key={index} width={context.getWidth(index)} className="FormGroup__item">
          {element}
        </FormGroupItem>
      ))}
    </FormGroupContentSlot>
  )
}

const FormCard = styled(BaseFormCard)`
  margin-bottom: ${uiTokens.spacing.padding.large}px;
  flex-shrink: 0;
`

interface Props {
  node: FormNode
  children?: ReactNode[]
}

export function FrameworkGroup({ node, children }: Props) {
  const groupDef = node.value.formDefinition as FormGroupDefinition
  const t = useTranslation()

  const caclWidh = (index: number) => {
    const item: any = node.id === 'ROOT' ? groupDef : groupDef.layout[index]
    return getWidthOfElement(item, groupDef, node)
  }

  const hasMandatory = () => {
    if (groupDef.showMandatory) {
      let result = false
      formTraverse(node, (item) => {
        const hasMandatoryFlag = nodeHasFlag(item, ControlValidationType.Mandatory)
        if (hasMandatoryFlag) result = true
      })
      return result
    }
    return false
  }

  const formCardHeader =
    groupDef.groupStyle === GroupStyle.FormCard && groupDef.idml && !groupDef.hideGroupHeader
      ? {
          title: (
            <TitleWrapper>
              {t(groupDef.idml, groupDef.description)}
              {hasMandatory() && <Mandatory>*</Mandatory>}
              {groupDef.tooltip && (
                <Tooltip title={t(groupDef.tooltip.idml, groupDef.tooltip.description)} placement="right" arrow>
                  <IconSelector iconName="InfoOutlined" color="primary" />
                </Tooltip>
              )}
            </TitleWrapper>
          ),
          subheader:
            groupDef.subtitle && groupDef.subtitle.idml && groupDef.subtitle.description
              ? t(groupDef.subtitle.idml, groupDef.subtitle.description)
              : undefined,
        }
      : undefined

  return (
    <>
      {node.value.visibility === 'Visible' && (
        <>
          {[Separator.Top, Separator.Both].includes(groupDef.separator) && <StyledSeparator />}
          {[EndToEndSeparator.Top, EndToEndSeparator.Both].includes(groupDef.endToEndSeparator) && (
            <StyledEndToEndSeparator />
          )}
          {groupDef.groupStyle === GroupStyle.FormCard && (
            <>
              {/*
                FormCard renders view/edit as component types. Inline slots would change identity on each
                render and remount fields; module-level slots cannot capture this group's children and width
                function. Context supplies that data while keeping the slot types stable. FormCard.children
                could hold the body, but that usage is not explicitly documented, so we use the view/edit slots.
              */}
              <FormCardSlotContext.Provider value={{ children, getWidth: caclWidh }}>
                <FormCard
                  className="FormGroup"
                  header={formCardHeader}
                  view={ContentSlot}
                  edit={ContentSlot}
                  mode={node.value.state === 'Readonly' ? 'view' : 'edit'}
                  showEditButton={false}
                >
                  {/*
                    <FormGroupContentSlot>
                      {children?.map((element, index) => (
                        <FormGroupItem key={index} width={caclWidh(index)} className="FormGroup__item">
                          {element}
                        </FormGroupItem>
                      ))}
                    </FormGroupContentSlot>
                  */}
                </FormCard>
              </FormCardSlotContext.Provider>
            </>
          )}
          {groupDef.groupStyle !== GroupStyle.FormCard && (
            <StyledWrapper $groupStyle={groupDef.groupStyle} className="FormGroup">
              {groupDef.idml && !groupDef.hideGroupHeader && (
                <HeaderWrapper>
                  <TitleWrapper>
                    <StyledCardHeader
                      title={
                        <span>
                          {t(groupDef.idml, groupDef.description)}
                          {hasMandatory() && <Mandatory>*</Mandatory>}
                        </span>
                      }
                      $groupStyle={groupDef.groupStyle}
                      className={groupDef.groupStyle == GroupStyle.CenterGroup ? 'centered-group' : ''}
                      titleTypographyProps={{
                        fontSize: groupDef.groupStyle === GroupStyle.SubGroup ? '16px' : '18px',
                        fontWeight: groupDef.groupStyle === GroupStyle.SubGroup ? '400' : '700',
                        padding: groupDef.groupStyle === GroupStyle.SubGroup ? '8px 0 8px 0' : '16px 0 8px 0',
                      }}
                    />
                    {groupDef.tooltip && (
                      <Tooltip title={t(groupDef.tooltip.idml, groupDef.tooltip.description)} placement="right" arrow>
                        <IconContainer>
                          <IconSelector iconName="InfoOutlined" color="primary" />
                        </IconContainer>
                      </Tooltip>
                    )}
                  </TitleWrapper>
                  {groupDef.subtitle && groupDef.subtitle.idml && groupDef.subtitle.description && (
                    <Typography variant="body2" color={COLORS.text.secondary}>
                      {t(groupDef.subtitle.idml, groupDef.subtitle.description)}
                    </Typography>
                  )}
                </HeaderWrapper>
              )}

              <FormGroupContent className="FormGroup__content">
                {children?.map((element, index) => (
                  <FormGroupItem key={index} width={caclWidh(index)} className="FormGroup__item">
                    {element}
                  </FormGroupItem>
                ))}
              </FormGroupContent>
            </StyledWrapper>
          )}

          {[Separator.Bottom, Separator.Both].includes(groupDef.separator) && <StyledSeparator />}
          {[EndToEndSeparator.Bottom, EndToEndSeparator.Both].includes(groupDef.endToEndSeparator) && (
            <StyledEndToEndSeparator />
          )}
        </>
      )}
    </>
  )
}
